/**
 * PEStart Aufbau-/Verbesserungsplan — read-only Bridge zum Coach-Planungsrechner (`planungsrechnerArchiv`).
 */

import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore from '../../store/usePEStartStore';
import {
    computePEStartAufbauPlan,
    PESTART_NO_AUFBAUPLAN_ID,
    type AufbauHebelKategorie,
    type PEStartAufbauPlanModel,
} from './peStartPlanungsBridge';

export type PEStartAufbauPlanProps = {
    className?: string;
};

const KAT_LABEL: Record<AufbauHebelKategorie, string> = {
    PREIS: 'Preis',
    MATERIAL: 'Material',
    PERSONAL: 'Personal',
    GEMEINKOSTEN: 'Betriebskosten',
    INVESTITION: 'Investition',
    DIENSTLEISTUNG: 'Leistung',
    SONSTIGE: 'Sonstiges',
};

const formatEuro = (v: number, digits = 0) =>
    v.toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

const formatDelta = (v: number) => {
    const sign = v > 0 ? '+' : '';
    return `${sign}${formatEuro(v)}`;
};

/**
 * Wiederverwendbare Auswahl-Box für den Coach-Planungsrechner:
 * - 0 Pläne: klarer Hinweis „Kein Aufbauplan vorhanden“
 * - 1 Plan: wird automatisch übernommen (mit Hinweis + optional „Ohne Aufbauplan“)
 * - >1 Pläne: deutliche Karten-Auswahl, welchen Planungsrechner-Stand man für dieses Szenario nutzt
 */
export const PEStartPlanungsrechnerPicker: React.FC<{
    model: PEStartAufbauPlanModel;
    selectedId: string | null | undefined;
    onSelect: (id: string | null) => void;
    compact?: boolean;
}> = ({ model, selectedId, onSelect, compact = false }) => {
    const count = model.szenarioOptions.length;

    if (count === 0) {
        return (
            <div className="rounded-xl border border-dashed border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-slate-400">
                            Aufbauplan · Planungsrechner (Coach)
                        </p>
                        <p className="mt-1 text-sm font-semibold text-pe-text dark:text-slate-200">
                            Kein Aufbauplan im Planungsrechner vorhanden
                        </p>
                        <p className="mt-0.5 text-xs text-stone-500 dark:text-slate-500">
                            Es ist aktuell kein Szenario im Coach-Planungsrechner gespeichert — PEStart rechnet ohne Aufbauplan.
                        </p>
                    </div>
                    <span className="rounded-md border border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        0 Pläne verfügbar
                    </span>
                </div>
            </div>
        );
    }

    const isNoneSelected = selectedId === PESTART_NO_AUFBAUPLAN_ID;
    const effectiveActiveId = isNoneSelected
        ? PESTART_NO_AUFBAUPLAN_ID
        : model.activeSzenarioId || model.szenarioOptions[0]?.id;

    if (count === 1) {
        const only = model.szenarioOptions[0];
        return (
            <div className="rounded-xl border border-[#E6C299]/35 bg-[#E6C299]/10 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                            Aufbauplan · Planungsrechner (Coach)
                        </p>
                        {!isNoneSelected ? (
                            <>
                                <p className="mt-1 text-sm font-bold text-pe-text dark:text-white">
                                    Automatisch übernommen: „{only.titel}“
                                </p>
                                <p className="mt-0.5 text-xs text-stone-600 dark:text-slate-300">
                                    Umsatz-Effekt: <strong>{formatDelta(only.umsatzDeltaMonat)} / Monat</strong> · Ergebnis:{' '}
                                    <strong>{formatDelta(only.ergebnisDeltaMonat)} / Monat</strong> ({only.hebelCount} Hebel)
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="mt-1 text-sm font-bold text-pe-text dark:text-white">
                                    Ohne Aufbauplan (Szenario „{only.titel}“ deaktiviert)
                                </p>
                                <p className="mt-0.5 text-xs text-stone-500 dark:text-slate-400">
                                    Du kannst den vorhandenen Planungsrechner-Stand jederzeit wieder aktivieren.
                                </p>
                            </>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onSelect(only.id)}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                !isNoneSelected
                                    ? 'border-[#E6C299] bg-[#E6C299] text-black'
                                    : 'border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/40'
                            }`}
                        >
                            Übernehmen
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelect(PESTART_NO_AUFBAUPLAN_ID)}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                isNoneSelected
                                    ? 'border-[#E6C299] bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299]'
                                    : 'border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] text-stone-500 dark:text-slate-400 hover:border-pe-border-strong'
                            }`}
                        >
                            Ohne Aufbauplan
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-[#E6C299]/35 bg-pe-elevated dark:bg-[#141414] p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                        Aufbauplan-Auswahl · Planungsrechner (Coach)
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-pe-text dark:text-white">
                        {count} Pläne im Planungsrechner verfügbar — bitte wähle das Szenario für diesen Businessplan:
                    </p>
                </div>
                <span className="rounded-md border border-[#E6C299]/40 bg-[#E6C299]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                    Aktiv: {isNoneSelected ? 'Ohne Aufbauplan' : model.szenarioName}
                </span>
            </div>

            <div className={`grid grid-cols-1 gap-2.5 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                {model.szenarioOptions.map((opt) => {
                    const active = effectiveActiveId === opt.id;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => onSelect(opt.id)}
                            className={`flex flex-col justify-between rounded-xl border p-3 text-left transition-all ${
                                active
                                    ? 'border-[#E6C299] bg-[#E6C299]/15 shadow-sm'
                                    : 'border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] hover:border-[#E6C299]/40'
                            }`}
                        >
                            <div>
                                <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm font-bold text-pe-text dark:text-white">
                                        {opt.titel}
                                    </span>
                                    {active && (
                                        <span className="shrink-0 rounded bg-[#E6C299] px-1.5 py-0.5 text-[9px] font-black uppercase text-black">
                                            Gewählt
                                        </span>
                                    )}
                                </div>
                                <p className="mt-0.5 text-[11px] text-stone-500 dark:text-slate-500">
                                    {new Date(opt.timestamp).toLocaleDateString('de-DE')} · {opt.hebelCount} Hebel
                                </p>
                            </div>
                            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-pe-border dark:border-[#222] pt-2 text-xs">
                                <span className="text-stone-500 dark:text-slate-400">
                                    Umsatz:{' '}
                                    <strong
                                        className={
                                            opt.umsatzDeltaMonat >= 0
                                                ? 'text-emerald-700 dark:text-emerald-400'
                                                : 'text-rose-500'
                                        }
                                    >
                                        {formatDelta(opt.umsatzDeltaMonat)}
                                    </strong>
                                </span>
                                <span className="text-stone-500 dark:text-slate-400">
                                    Ergebnis:{' '}
                                    <strong
                                        className={
                                            opt.ergebnisDeltaMonat >= 0
                                                ? 'text-emerald-700 dark:text-emerald-400'
                                                : 'text-rose-500'
                                        }
                                    >
                                        {formatDelta(opt.ergebnisDeltaMonat)}
                                    </strong>
                                </span>
                            </div>
                        </button>
                    );
                })}

                <button
                    type="button"
                    onClick={() => onSelect(PESTART_NO_AUFBAUPLAN_ID)}
                    className={`flex flex-col justify-between rounded-xl border p-3 text-left transition-all ${
                        isNoneSelected
                            ? 'border-[#E6C299] bg-[#E6C299]/15'
                            : 'border-dashed border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] hover:border-[#E6C299]/40'
                    }`}
                >
                    <div>
                        <div className="flex items-start justify-between gap-2">
                            <span className="text-sm font-bold text-pe-muted dark:text-slate-300">
                                Ohne Aufbauplan rechnen
                            </span>
                            {isNoneSelected && (
                                <span className="shrink-0 rounded bg-[#E6C299] px-1.5 py-0.5 text-[9px] font-black uppercase text-black">
                                    Gewählt
                                </span>
                            )}
                        </div>
                        <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                            Keine Planungsrechner-Hebel einrechnen (nur Basis-Planziel).
                        </p>
                    </div>
                </button>
            </div>
        </div>
    );
};

const PEStartAufbauPlan: React.FC<PEStartAufbauPlanProps> = ({ className }) => {
    const calc = useCalculationsSelector() as any;
    const clientData = useAppStore((s) => s.clientData);
    const setClientData = useAppStore((s) => s.setClientData);
    const settings = useAppStore((s) => s.settings);
    const selectedPlanungsrechnerSzenarioId = usePEStartStore(
        (s) => s.selectedPlanungsrechnerSzenarioId
    );
    const setSelectedPlanungsrechnerSzenarioId = usePEStartStore(
        (s) => s.setSelectedPlanungsrechnerSzenarioId
    );

    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const model = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, selectedPlanungsrechnerSzenarioId]
    );

    const handleSelectSzenario = (id: string | null) => {
        setSelectedPlanungsrechnerSzenarioId(id);
        if (typeof setClientData === 'function') {
            setClientData((prev: any) => ({
                ...prev,
                peStartSelectedPlanungsrechnerId: id,
            }));
        }
    };

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Schritt 3
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    Aufbau- &amp; Verbesserungsplan
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Wähle hier aus, welches Szenario aus dem Coach-Planungsrechner für diesen Businessplan übernommen wird.
                </p>
            </header>

            <div className="mb-6">
                <PEStartPlanungsrechnerPicker
                    model={model}
                    selectedId={selectedPlanungsrechnerSzenarioId}
                    onSelect={handleSelectSzenario}
                />
            </div>

            {model.available && model.summary && (
                <>
                    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                Umsatz / Monat
                            </p>
                            <p className="mt-2 text-xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                                {formatEuro(model.summary.umsatzPrognoseMonat)}
                            </p>
                            <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                                Basis {formatEuro(model.summary.umsatzIstMonat)} · Δ{' '}
                                <span
                                    className={
                                        model.summary.umsatzDeltaMonat >= 0
                                            ? 'text-emerald-800 dark:text-emerald-400'
                                            : 'text-rose-800 dark:text-rose-400'
                                    }
                                >
                                    {formatDelta(model.summary.umsatzDeltaMonat)}
                                </span>
                            </p>
                        </div>
                        <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                Betriebsergebnis / Monat
                            </p>
                            <p className="mt-2 text-xl font-black tabular-nums text-pe-text dark:text-white">
                                {formatEuro(model.summary.ergebnisPrognoseMonat)}
                            </p>
                            <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                                Basis {formatEuro(model.summary.ergebnisIstMonat)} · Δ{' '}
                                <span
                                    className={
                                        model.summary.ergebnisDeltaMonat >= 0
                                            ? 'text-emerald-800 dark:text-emerald-400'
                                            : 'text-rose-800 dark:text-rose-400'
                                    }
                                >
                                    {formatDelta(model.summary.ergebnisDeltaMonat)}
                                </span>
                            </p>
                        </div>
                        <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                Rentabilität
                            </p>
                            <p className="mt-2 text-xl font-black tabular-nums text-pe-text dark:text-white">
                                {model.summary.rentabilitaetPrognose.toFixed(1)} %
                            </p>
                            <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                                Basis {model.summary.rentabilitaetIst.toFixed(1)} %
                                {model.summary.preisAnpassungProzent !== 0 && (
                                    <>
                                        {' '}
                                        · Preis{' '}
                                        {model.summary.preisAnpassungProzent > 0 ? '+' : ''}
                                        {model.summary.preisAnpassungProzent.toFixed(1)} %
                                    </>
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="mb-2 flex items-baseline justify-between gap-2">
                        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-pe-muted dark:text-slate-400">
                            Hebel im Szenario „{model.szenarioName}“ ({model.hebel.length})
                        </h3>
                        <p className="text-[10px] text-slate-600">
                            Nur Anzeige — Bearbeitung im Planungsrechner (Coach)
                        </p>
                    </div>

                    {model.hebel.length === 0 ? (
                        <p className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4 text-sm text-stone-500 dark:text-slate-500">
                            Szenario erkannt, aber keine konkreten Hebel-Positionen im Planungsrechner aktiviert.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {model.hebel.map((h) => (
                                <li
                                    key={h.id}
                                    className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="rounded border border-[#E6C299]/30 bg-[#E6C299]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                                            {KAT_LABEL[h.kategorie]}
                                        </span>
                                        <span className="text-sm font-semibold text-pe-text dark:text-slate-200">
                                            {h.titel}
                                        </span>
                                    </div>
                                    <p className="mt-1.5 text-sm text-pe-muted dark:text-slate-400">{h.detail}</p>
                                    {h.wirkungHinweis && (
                                        <p className="mt-1 text-xs text-slate-600">{h.wirkungHinweis}</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
};

export default PEStartAufbauPlan;
